// src/components/AccessRequestForm.jsx
//
// Reached from Login.jsx's "Don't have account to login? Raise Access
// Request" link. Two in-page steps sharing one URL (/access-request):
//   1. ACCESS REQUEST form -- Full Name, Email Address, Mobile, Company,
//      Department -> POST /access-requests.
//   2. Success confirmation -- green "success" alert + the two message
//      lines from the mockup, with a "Back to Login" link.
//
// Rebuilt to match C:\CET-HTML-Code\sign-up.html field-for-field (same
// auth-box/row/col-md-7/col-md-5 shell Login.jsx already uses, same
// Full Name/Email Address/Mobile/Company/Department fields with the same
// app-search + icon textbox style and field order) instead of the old
// one-off grid-row/form-control-inline layout (Login.css) that never
// matched the Login page's actual textbox size/style. Two deliberate
// departures from the raw mockup, both carried over from the previous
// version of this file for good reason:
//   - Company stays a <select> sourced from the Company master table
//     (GET /companies) rather than the mockup's plain text input --
//     every other Company field in the app is a real FK-backed dropdown,
//     and free-text company names would break that data integrity.
//   - The illustration keeps the existing gauge-illustration.png asset
//     (the "COST / LOW-HIGH dial" image already used here) rather than
//     the mockup's cost-banner.jpg, which isn't part of this project's
//     asset bundle.
//
// Backend inserts the submission into a new AccessRequest staging
// table (NOT directly into Users -- these are unvetted, no RoleID
// yet) and emails the details to razihaider2005@gmail.com so it isn't
// missed. See access_request_table.sql and the /access-requests route
// in app.py.
//
// NOTE: the "Super Admin gets an in-app notification about this on
// login" part of the request was explicitly deferred by the customer
// ("notification part latter update") -- only the email notification
// is implemented here.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_ORIGIN } from "../config/apiConfig";

export default function AccessRequestForm() {
  const [step, setStep] = useState(1); // 1 = form, 2 = success

  const [companies, setCompanies] = useState([]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [department, setDepartment] = useState("");
  const [companyId, setCompanyId] = useState("");

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_ORIGIN}/companies`)
      .then((res) => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCompanies([]));
  }, []);

  // Basic email format check -- something@something.tld, no spaces.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Mobile: digits with optional leading +, spaces, dashes, min 7 digits
  // total so "123" doesn't slip through, matches the "+1 234 567 8900"
  // placeholder style without being overly strict about country formats.
  const MOBILE_RE = /^\+?[0-9\s-]{7,20}$/;
  // Department name: letters, spaces, and & / - only -- no digits or
  // stray symbols (keeps out things like "123" or "asdf!!").
  const DEPARTMENT_RE = /^[A-Za-z][A-Za-z\s&\-\/]*$/;

  const submitRequest = async (e) => {
    e.preventDefault();
    setMsg("");

    if (!name.trim() || !email.trim()) {
      setMsg("Please fill in Name and Email ID");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setMsg("Please enter a valid Email Address (e.g. you@example.com)");
      return;
    }
    if (!mobileNo.trim()) {
      setMsg("Please fill in Mobile No.");
      return;
    }
    if (!MOBILE_RE.test(mobileNo.trim())) {
      setMsg("Please enter a valid Mobile No. (digits only, 7-20 characters)");
      return;
    }
    if (!companyId) {
      setMsg("Please select a Company");
      return;
    }
    if (!department.trim()) {
      setMsg("Please fill in Department");
      return;
    }
    if (!DEPARTMENT_RE.test(department.trim())) {
      setMsg("Please enter a valid Department name (letters only)");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API_ORIGIN}/access-requests`, {
        Name: name.trim(),
        Email: email.trim(),
        MobileNo: mobileNo.trim(),
        Department: department.trim(),
        CompanyID: companyId || null,
      });
      if (res.data && res.data.success) {
        setStep(2);
      } else {
        setMsg(res.data?.message || "Unable to submit access request");
      }
    } catch (err) {
      setMsg(err.response?.data?.message || "Server error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-box p-0 w-100">
      <div className="row w-100 g-0">
        <div className="col-md-7 d-none d-md-block">
          <div
            className="h-100 position-relative card-side-img rounded-0 overflow-hidden"
            style={{ backgroundImage: "url(/assets/images/logo/CET%20Background-withoutlogo.png)", backgroundSize: "cover", backgroundPosition: "center" }}
          >
            <div className="p-4 card-img-overlay auth-overlay d-flex align-items-end justify-content-center" />
          </div>
        </div>

        <div className="col-md-5">
          <div className="card auth-box-form border-0 mb-0">
            <div className="card-body min-vh-100 position-relative d-flex flex-column justify-content-center">
              <div className="auth-brand mb-3 text-center">
                <img
                  src="/assets/images/logo/CET-Full-logo.png"
                  alt="Logo"
                  style={{ maxWidth: 220 }}
                />
              </div>

              {step === 1 && (
                <>
                  <div className="text-center">
                    <h3 className="fw-bold text-dark">Access Request Form</h3>
                  </div>

                  {msg && (
                    <div className="alert alert-danger py-2 mt-3 mb-0" role="alert" aria-live="polite">
                      {msg}
                    </div>
                  )}

                  <form className="mt-4" onSubmit={submitRequest}>
                    <div className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <div className="app-search">
                        <input
                          id="name"
                          type="text"
                          className="form-control"
                          placeholder="Enter your name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                        <i className="ti ti-user-circle app-search-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <div className="app-search">
                        <input
                          id="email"
                          type="email"
                          className="form-control"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          autoComplete="username"
                          required
                        />
                        <i className="ti ti-mail app-search-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="mobileNo" className="form-label">
                        Mobile <span className="text-danger">*</span>
                      </label>
                      <div className="app-search">
                        <input
                          id="mobileNo"
                          type="tel"
                          className="form-control"
                          placeholder="+1 234 567 8900"
                          value={mobileNo}
                          onChange={(e) => setMobileNo(e.target.value)}
                          inputMode="tel"
                          required
                        />
                        <i className="ti ti-phone app-search-icon" />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="company" className="form-label">
                        Company <span className="text-danger">*</span>
                      </label>
                      <select
                        id="company"
                        className="form-select"
                        value={companyId}
                        onChange={(e) => setCompanyId(e.target.value)}
                        required
                      >
                        <option value="">-- Select Company --</option>
                        {companies.map((c) => (
                          <option key={c.CompanyID} value={c.CompanyID}>
                            {c.CompanyName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="department" className="form-label">
                        Department <span className="text-danger">*</span>
                      </label>
                      <div className="app-search">
                        <input
                          id="department"
                          type="text"
                          className="form-control"
                          placeholder="Enter your department"
                          value={department}
                          onChange={(e) => setDepartment(e.target.value)}
                          required
                        />
                        <i className="ti ti-briefcase app-search-icon" />
                      </div>
                    </div>

                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary fw-semibold py-2" disabled={loading}>
                        {loading ? "Submitting..." : "Submit Request"}
                      </button>
                    </div>
                  </form>

                  <p className="text-muted text-center mt-4 mb-0">
                    Already have an account?{" "}
                    <a href="/" className="link-offset-3 fw-bold">Login</a>
                  </p>
                </>
              )}

              {step === 2 && (
                <>
                  <div className="alert alert-success mt-4" role="alert">
                    Access request submitted successfully.
                    <br />
                    The Cost Estimation Tool team will review and provide required access.
                    <br />
                    You can follow-up at :{" "}
                    <a href="mailto:cet@costestimationtool.online">cet@costestimationtool.online</a>
                  </div>

                  <p className="text-muted text-center mt-4 mb-0">
                    <a href="/" className="link-offset-3 fw-bold">Back to Login</a>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
